import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from './ui/card';
import { ApiService } from '../utils/api';
import { Loader2 } from 'lucide-react';

const QRScanStats = ({ eventId, purpose }) => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['qrScanStats', eventId, purpose],
    queryFn: () => ApiService.getQRScanPurposeStats(eventId, purpose),
    enabled: !!eventId && !!purpose,
    refetchInterval: 5000 // Refresh every 5 seconds
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <Card className="bg-gray-50">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Scan Stats */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Scan Statistics</h3>
            <div className="flex items-center justify-between bg-white p-3 rounded-lg border">
              <div>
                <p className="text-sm text-gray-600">Total Scans</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalScans || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Last Scan</p>
                <p className="text-sm text-gray-900">{stats.lastScanTime || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Inventory Stats */}
          {stats.hasInventory && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Inventory Tracking</h3>
              <div className="bg-white p-3 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Total Inventory</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats.totalInventory === null ? '∞' : stats.totalInventory}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Used</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats.totalInventory === null ? stats.totalScans : (stats.totalInventory - stats.remainingInventory)}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Remaining</p>
                  <p className={`text-lg font-semibold ${
                    stats.remainingInventory > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stats.totalInventory === null ? '∞' : stats.remainingInventory}
                  </p>
                </div>
                {stats.totalInventory !== null && (
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          stats.remainingInventory > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (stats.remainingInventory / stats.totalInventory) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QRScanStats;
